import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import {
  getRoomFields,
  createRoomField,
  updateRoomField,
  deleteRoomField,
  initializeSystemFields,
  reorderRoomFields,
} from '../services/roomFieldService';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select (Dropdown)' },
  { value: 'boolean', label: 'Yes/No' },
];

// Sortable Item Component
function SortableFieldItem({ field, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mr: 2, 
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' }
        }}
        {...attributes}
        {...listeners}
      >
        <DragIcon color="action" />
      </Box>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1">{field.label}</Typography>
            <Chip 
              label={field.type} 
              size="small" 
              variant="outlined"
            />
            {field.required && (
              <Chip 
                label="Required" 
                size="small" 
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            {field.description && (
              <Typography variant="body2" color="text.secondary">
                Description: {field.description}
              </Typography>
            )}
            {field.type === 'select' && field.options && field.options.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Options: {field.options.join(', ')}
              </Typography>
            )}
            {field.defaultValue && (
              <Typography variant="body2" color="text.secondary">
                Default: {field.defaultValue}
              </Typography>
            )}
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <IconButton
          edge="end"
          onClick={() => onEdit(field)}
          sx={{ mr: 1 }}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          edge="end"
          onClick={() => onDelete(field)}
        >
          <DeleteIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );
}

export default function RoomFieldsTab() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    type: 'text',
    required: false,
    options: [''],
    defaultValue: '',
  });
  const { enqueueSnackbar } = useSnackbar();
  
  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  const isAdmin = user?.role === 'admin';

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadRoomFields = useCallback(async () => {
    try {
      setLoading(true);
      const fieldsData = await getRoomFields();
      setFields(fieldsData || []);

      // If no fields exist, initialize system fields
      if (!fieldsData || fieldsData.length === 0) {
        await initializeSystemFields();
        const newFieldsData = await getRoomFields();
        setFields(newFieldsData || []);
        enqueueSnackbar('Default fields initialized successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error loading room fields:', error);
      enqueueSnackbar('Failed to load room fields', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    // Only load fields if user is authenticated and is an admin
    if (user && isAdmin) {
      loadRoomFields();
    } else if (user && !isAdmin) {
      setLoading(false);
    }
  }, [user, isAdmin, loadRoomFields]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field) => field._id === active.id);
      const newIndex = fields.findIndex((field) => field._id === over.id);

      const newFields = arrayMove(fields, oldIndex, newIndex);
      setFields(newFields);

      try {
        // Send the new order to the server
        const fieldIds = newFields.map(field => field._id);
        await reorderRoomFields(fieldIds);
        enqueueSnackbar('Field order updated successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error reordering fields:', error);
        enqueueSnackbar('Failed to update field order', { variant: 'error' });
        // Revert the order on error
        loadRoomFields();
      }
    }
  };

  const handleOpenDialog = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        name: field.name,
        label: field.label,
        description: field.description || '',
        type: field.type,
        required: field.required || false,
        options: field.type === 'select' ? field.options || [''] : [''],
        defaultValue: field.defaultValue || '',
      });
    } else {
      setEditingField(null);
      setFormData({
        name: '',
        label: '',
        description: '',
        type: 'text',
        required: false,
        options: [''],
        defaultValue: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingField(null);
    setFormData({
      name: '',
      label: '',
      description: '',
      type: 'text',
      required: false,
      options: [''],
      defaultValue: '',
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionsChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.label.trim()) {
        enqueueSnackbar('Label is required', { variant: 'error' });
        return;
      }

      const fieldData = {
        label: formData.label.trim(),
        description: formData.description.trim(),
        type: formData.type,
        required: formData.required,
      };

      // Only include name for updates, let backend generate it for new fields
      if (editingField) {
        fieldData.name = formData.name;
      }

      if (formData.type === 'select') {
        fieldData.options = formData.options.filter(opt => opt.trim());
        if (fieldData.options.length === 0) {
          enqueueSnackbar('Select fields must have at least one option', { variant: 'error' });
          return;
        }
      }

      if (formData.defaultValue) {
        fieldData.defaultValue = formData.defaultValue;
      }

      let result;
      if (editingField) {
        result = await updateRoomField(editingField._id, fieldData);
      } else {
        result = await createRoomField(fieldData);
      }

      enqueueSnackbar(result.message || `Field ${editingField ? 'updated' : 'created'} successfully`, { 
        variant: 'success' 
      });
      
      handleCloseDialog();
      loadRoomFields();
    } catch (error) {
      console.error('Error saving field:', error);
      enqueueSnackbar(
        error.response?.data?.error || `Failed to ${editingField ? 'update' : 'create'} field`,
        { variant: 'error' }
      );
    }
  };

  const handleDelete = async (field) => {
    if (window.confirm(`Are you sure you want to delete the field "${field.label}"?`)) {
      try {
        const result = await deleteRoomField(field._id);
        enqueueSnackbar(result.message || 'Field deleted successfully', { variant: 'success' });
        loadRoomFields();
      } catch (error) {
        console.error('Error deleting field:', error);
        enqueueSnackbar(
          error.response?.data?.error || 'Failed to delete field',
          { variant: 'error' }
        );
      }
    }
  };

  // Show loading if user is not loaded yet
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Show error if user is not admin
  if (!isAdmin) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            Only organization administrators can manage room fields. Please contact your admin to modify room field settings.
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography>Loading room fields...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Room Fields Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Field
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the fields that will be available when creating and editing rooms. 
        Drag and drop to reorder fields. All fields can be modified or deleted as needed.
      </Typography>

      <Card>
        <CardContent>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={fields.map(field => field._id)}
              strategy={verticalListSortingStrategy}
            >
              <List>
                {fields.map((field, index) => (
                  <React.Fragment key={field._id}>
                    <SortableFieldItem
                      field={field}
                      onEdit={handleOpenDialog}
                      onDelete={handleDelete}
                    />
                    {index < fields.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </SortableContext>
          </DndContext>

          {fields.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No fields configured yet. Click "Add Field" to get started.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Field Editor Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingField ? `Edit Field: ${editingField.label}` : 'Add New Field'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Label"
                value={formData.label}
                onChange={(e) => handleInputChange('label', e.target.value)}
                fullWidth
                required
                helperText="Display name for this field"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                multiline
                rows={2}
                helperText="Helper text shown below the field in room forms"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Field Type"
                >
                  {FIELD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.required}
                    onChange={(e) => handleInputChange('required', e.target.checked)}
                  />
                }
                label="Required field"
              />
            </Grid>

            {formData.type === 'select' && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Options
                </Typography>
                {formData.options.map((option, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TextField
                      value={option}
                      onChange={(e) => handleOptionsChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      size="small"
                      fullWidth
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      onClick={() => removeOption(index)}
                      disabled={formData.options.length <= 1}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addOption}
                >
                  Add Option
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                label="Default Value"
                value={formData.defaultValue}
                onChange={(e) => handleInputChange('defaultValue', e.target.value)}
                fullWidth
                helperText="Optional default value for new rooms"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingField ? 'Update' : 'Create'} Field
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 